import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent, RefObject } from "react";
import type { PointModel } from "@patent-studio/api-types";

import { useEscapeScope } from "@/components/ui/escape-scope";

/**
 * How far the pointer travels before a press becomes a drag, in screen pixels.
 *
 * A pin is a control as well as a thing to move: pressing one selects it. Without a
 * threshold every selection would end in a write of the pointer's own position over the
 * stored one, because a press and a release a pixel apart are still two different points.
 *
 * Shared with the pan gesture rather than copied into it. A press on the artwork may turn
 * out to be either, and an attorney's hand does not know which one it is starting.
 */
export const DRAG_THRESHOLD = 3;

/** Where a pointer event landed, as fractions of the box it is measured against. */
export function pointIn(
  layer: HTMLElement,
  event: { clientX: number; clientY: number },
): PointModel | undefined {
  const box = layer.getBoundingClientRect();
  // A drawing with no width has not been laid out. Dividing by it gives NaN, which cannot
  // be drawn and which the API refuses, so it must not become a point.
  if (box.width === 0 || box.height === 0) return undefined;
  return {
    x: (event.clientX - box.left) / box.width,
    y: (event.clientY - box.top) / box.height,
  };
}

/** The box a drawing's fractions are drawn into, which is what turns them into distances. */
export interface DrawnBox {
  width: number;
  height: number;
}

/**
 * The live size of the drawing, for the few things that are sized in pixels rather than in
 * fractions of it.
 *
 * Watched rather than read once: the same drawing is shown at panel size and at full size,
 * and nothing remounts in between. Held only when it has actually moved, because a fresh
 * value redraws every mark on the drawing and a resize often reports the same box.
 */
export function useDrawnBox(layer: RefObject<HTMLElement | null>) {
  const [box, setBox] = useState<DrawnBox | undefined>(undefined);
  useEffect(() => {
    const measured = layer.current;
    if (measured === null) return undefined;
    const watch = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setBox((held) =>
        held?.width === width && held.height === height
          ? held
          : { width, height },
      );
    });
    watch.observe(measured);
    return () => {
      watch.disconnect();
    };
  }, [layer]);
  return box;
}

const onDrawing = (value: number) => Math.min(1, Math.max(0, value));

/**
 * What a gesture picked up: one part of one mark.
 *
 * A bend names which of them it is, because a curve may be bent at two points and the two
 * handles would otherwise be indistinguishable to everything downstream.
 */
export type Grabbed =
  | { kind: "pin"; id: string }
  | { kind: "tip"; id: string }
  | { kind: "control"; id: string; index: number };

/** A gesture in progress, and where the thing it picked up has been dragged to. */
export interface Held {
  grabbed: Grabbed;
  at: PointModel;
}

interface Gripped extends Held {
  /** Where the stored point sat relative to the pointer, so the thing does not jump. */
  offset: PointModel;
  /** Where the press landed on screen, which is what the threshold is measured from. */
  from: { x: number; y: number };
  moved: boolean;
  /** The pointer that started this, so a second finger cannot move or finish it. */
  pointerId: number;
}

/**
 * Where the grabbed thing sits for this event, or nothing while the press is still a click.
 *
 * Kept on the drawing, so what the attorney let go of and what gets written down are the
 * same point. The rules clamp too, but only one of the two is on screen.
 */
function draggedTo(
  box: HTMLElement,
  event: { clientX: number; clientY: number },
  held: Gripped,
): PointModel | undefined {
  const far =
    Math.hypot(event.clientX - held.from.x, event.clientY - held.from.y) >=
    DRAG_THRESHOLD;
  if (!far && !held.moved) return undefined;
  const point = pointIn(box, event);
  if (point === undefined) return undefined;
  return {
    x: onDrawing(point.x + held.offset.x),
    y: onDrawing(point.y + held.offset.y),
  };
}

/**
 * Any new press means the click that follows it is a choice again.
 *
 * Without this the guard on the drawing would swallow every deselect after the first drag,
 * because a plain click on the drawing never starts a gesture that could clear the flag.
 */
function useForgetOnPress(draggedRef: RefObject<boolean>) {
  useEffect(() => {
    function pressed() {
      draggedRef.current = false;
    }
    window.addEventListener("pointerdown", pressed);
    return () => {
      window.removeEventListener("pointerdown", pressed);
    };
  }, [draggedRef]);
}

/**
 * One drag at a time, over the drawing, committing once on release.
 *
 * The gesture owns the value while it is live and the set owns it otherwise, which is the
 * contract the half-typed title field already follows. Exactly one change is issued, on
 * release, rather than one per pointer move: every entry point behind this queues a write of
 * the whole set, and a pointer reports dozens of moves a second.
 *
 * The moves and the release are watched on the window rather than captured on the handle.
 * A pointer that has left a fourteen pixel handle is still dragging what it picked up, and
 * the gesture is the only thing on the page while it runs.
 *
 * The gesture itself lives in a ref and only its position is kept in state, for two reasons.
 * The listeners then go on once when the gesture starts rather than being torn down and put
 * back on every move. And each event reads what the one before it wrote, rather than what
 * the last render happened to see: a release arriving before the browser has repainted the
 * move before it would otherwise commit a stale point, or read a real drag as a click and
 * commit nothing at all.
 *
 * A gesture whose mark has left the set is ended by its caller through `letGo`. The handle
 * unmounting is not enough on its own, because this hook lives above the handles and would
 * still be holding a mark that is gone when the pointer came up.
 */
export function useDrag(
  layer: RefObject<HTMLElement | null>,
  onCommit: (grabbed: Grabbed, at: PointModel) => void,
) {
  const gripRef = useRef<Gripped | undefined>(undefined);
  const [live, setLive] = useState(false);
  const [held, setHeld] = useState<Held | undefined>(undefined);
  // Whether the click now arriving is the tail of a drag rather than a choice. Held in a ref
  // because it is read when that click lands, not when the page last drew.
  const draggedRef = useRef(false);
  const commitRef = useRef(onCommit);
  useEffect(() => {
    commitRef.current = onCommit;
  });

  const letGo = useCallback(() => {
    gripRef.current = undefined;
    setLive(false);
    setHeld(undefined);
  }, []);

  // Escape abandons the gesture, sharing the key with everything else that answers it.
  useEscapeScope("gesture", live, letGo);

  useForgetOnPress(draggedRef);

  useEffect(() => {
    if (!live) return undefined;

    function moved(event: PointerEvent) {
      const held = gripRef.current;
      const box = layer.current;
      if (held === undefined || box === null) return;
      if (event.pointerId !== held.pointerId) return;
      const at = draggedTo(box, event, held);
      if (at === undefined) return;
      gripRef.current = { ...held, at, moved: true };
      setHeld({ grabbed: held.grabbed, at });
    }

    function released(event: PointerEvent) {
      if (gripRef.current?.pointerId !== event.pointerId) return;
      // The release carries a position of its own, and on a flick it can be the only one
      // past the threshold.
      moved(event);
      const settled = gripRef.current;
      letGo();
      if (!settled.moved) return;
      draggedRef.current = true;
      commitRef.current(settled.grabbed, settled.at);
    }

    function taken(event: PointerEvent) {
      // Cancelling writes nothing rather than committing what the gesture had reached. A
      // cancelled pointer is the platform taking the gesture away, a touch becoming a scroll
      // or a window losing focus mid-drag, which is the platform saying it did not finish.
      // That is the same thing Escape says, so it gets the same answer. Committing here
      // would write a position the attorney never let go of.
      if (gripRef.current?.pointerId === event.pointerId) letGo();
    }

    window.addEventListener("pointermove", moved);
    window.addEventListener("pointerup", released);
    window.addEventListener("pointercancel", taken);
    return () => {
      window.removeEventListener("pointermove", moved);
      window.removeEventListener("pointerup", released);
      window.removeEventListener("pointercancel", taken);
    };
  }, [live, layer, letGo]);

  return {
    /** The live gesture, for the drawing to layer over the set it was given. */
    held,

    /** Whether the click now arriving is the tail of a drag rather than a choice. */
    dragged: useCallback(() => draggedRef.current, []),

    /** Drops the gesture where it stands, writing nothing. */
    letGo,

    begin: (
      event: ReactPointerEvent<HTMLElement>,
      grabbed: Grabbed,
      stored: PointModel,
    ) => {
      const box = layer.current;
      if (box === null) return;
      const point = pointIn(box, event);
      if (point === undefined) return;
      // The drawing beneath reads a press on it as a press on nothing, and clearing the
      // selection would take this handle off the page mid-gesture.
      event.stopPropagation();
      gripRef.current = {
        grabbed,
        at: stored,
        offset: { x: stored.x - point.x, y: stored.y - point.y },
        from: { x: event.clientX, y: event.clientY },
        moved: false,
        pointerId: event.pointerId,
      };
      setLive(true);
      setHeld({ grabbed, at: stored });
    },
  };
}
